import { useQuery } from '@apollo/client';
import { gql } from '__generated__';
import { DepartmentAdminType } from '__generated__/graphql';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';
import LoadingIndicator from 'components/LoadingIndicator';

import CreateDepartmentAdminDialogButton from './CreateDepartmentAdminDialogButton';
import UpdateDepartmentAdminDialog from './UpdateDepartmentAdminDialog';

const DEPARTMENT_ADMINS_QUERY = gql(`
  query DepartmentAdmins($departmentId: ID!) {
    department(id: $departmentId) {
      id
      departmentAdmins {
        nodes {
          id
          user {
            id
            fullName
          }
          role
          createdAt
        }
        pageInfo {
          cursor
          totalCount
          hasNextPage
        }
      }
    }
  }
`);

const DepartmentAdminsPage: FC = () => {
  const { departmentId } = useParams<{ departmentId: string }>();
  const [departmentAdminOnDisplay, setDepartmentAdminOnDisplay] =
    useState<DepartmentAdminType | null>(null);

  const { loading, error, data, refetch, fetchMore } = useQuery(DEPARTMENT_ADMINS_QUERY, {
    variables: {
      departmentId: departmentId as string,
    },
    nextFetchPolicy: 'network-only', // This ensures empolyee count is up to date
  });
  if (loading) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const departmentAdmins = data.department.departmentAdmins.nodes;
  const pageInfo = data.department.departmentAdmins.pageInfo;

  return (
    <>
      <CreateDepartmentAdminDialogButton />
      {departmentAdminOnDisplay ? (
        <UpdateDepartmentAdminDialog
          id={departmentAdminOnDisplay.id}
          fullName={departmentAdminOnDisplay.user.fullName}
          role={departmentAdminOnDisplay.role}
          onClose={() => setDepartmentAdminOnDisplay(null)}
        />
      ) : null}
      <DataTable
        data={departmentAdmins as DepartmentAdminType[]}
        onClick={departmentAdmin => setDepartmentAdminOnDisplay(departmentAdmin)}
        columns={[
          {
            label: 'Full Name',
            fieldName: 'user.fullName',
          },
          {
            label: 'Role',
            fieldName: 'role',
          },
          {
            label: 'Created At',
            fieldName: 'createdAt',
          },
        ]}
        paginationLoading={loading}
        hasNextPage={pageInfo.hasNextPage as boolean}
        onLoadMore={() =>
          fetchMore({
            variables: {
              cursor: pageInfo.cursor,
            },
          })
        }
      />
    </>
  );
};

export default DepartmentAdminsPage;
